import {EstimateState} from '@/model/api';
import {ParameterValues} from '@/model/api/parameters';
import {NetworkError} from '@/model/errors/NetworkError';
import {Estimate} from '@/model/Estimate';
import {StagedParameters} from '@/model/offline/StagedParameters';

export class OfflineManager<T extends EstimateState = EstimateState> {
    public readonly stagedParameters: StagedParameters;
    private readonly server: Estimate<T>;

    constructor(stagedParameters: StagedParameters, server: Estimate<T>) {
        this.stagedParameters = stagedParameters;
        this.server = server;
    }

    /**
     * Returns fresh estimate from server after connection was reestablished
     */
    public waitForConnection(): Promise<T> {
        return new Promise((resolve, reject) => {
            const interval = window.setInterval(() => {
                this.server.getEstimate()
                    .then(estimate => {
                        window.clearInterval(interval);
                        return resolve(estimate);
                    }).catch(error => {
                    if (!(error instanceof NetworkError)) {
                        reject(error);
                    }
                });
            }, 5000);
        });
    }

    public isInSyncWith(estimate: T): boolean {
        return this.isEmpty(this.stagedParameters.diff(estimate));
    }

    public syncWithServer(lastKnownEstimateState: T): Promise<T> {
        const changedParameters = this.stagedParameters.diff(lastKnownEstimateState);

        if (this.isEmpty(changedParameters)) {
            return Promise.resolve(lastKnownEstimateState);
        }

        return this.server.updateParameters(changedParameters)
            .then(estimate => {
                this.stagedParameters.clear();

                return estimate;
            });
    }

    public clear(): void {
        this.stagedParameters.clear();
    }

    private isEmpty(parameters: ParameterValues): boolean {
        return Object.keys(parameters).length === 0;
    }
}
